import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../utils/api';
import { syncGoogleFitSteps } from '../utils/fitnessSync';
import { 
  Footprints, 
  Smartphone, 
  Link2, 
  Link2Off, 
  ChevronRight, 
  ChevronLeft,
  Apple, 
  Activity,
  Target,
  TrendingUp,
  Calendar,
  Plus,
  Check,
  AlertCircle,
  ExternalLink,
  Info,
  RefreshCw,
  Clock
} from 'lucide-react';
import { confirmDialog } from '../components/ui/confirm-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Theme colors
const COLORS = {
  primary: '#5DBFAD',
  primaryLight: '#E8F5F3',
  primaryDark: '#4CA899',
  orange: '#F59E0B',
  orangeLight: '#FEF7ED',
  purple: '#8B5CF6',
  purpleLight: 'rgba(139, 92, 246, 0.1)',
  text: '#1F2937',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  border: '#E5E7EB',
  background: '#F9FAFB',
  white: '#FFFFFF',
  success: '#10B981',
  error: '#EF4444'
};

export const StepTrackingPage = ({ TopHeader, BottomNav }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, configured: false });
  const [todaySteps, setTodaySteps] = useState({ steps: 0, goal: 10000, progress_percentage: 0 });
  const [weeklyData, setWeeklyData] = useState([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualSteps, setManualSteps] = useState('');
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showGoogleInstructions, setShowGoogleInstructions] = useState(false);
  const [notification, setNotification] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncResult, setSyncResult] = useState(null); // { success: bool, stepsFound: number }

  useEffect(() => {
    fetchData();
    
    // Check for OAuth callback status
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    
    if (connected === 'true') {
      setNotification({ type: 'success', message: 'Google Fit connected successfully!' });
      // Clear URL params
      window.history.replaceState({}, '', '/steps');
    } else if (error) {
      setNotification({ type: 'error', message: 'Failed to connect Google Fit. Please try again.' });
      window.history.replaceState({}, '', '/steps');
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, todayRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/fitness/google/status`, { headers }),
        axios.get(`${API}/fitness/steps/today`, { headers }),
        axios.get(`${API}/fitness/steps/weekly`, { headers })
      ]);
      
      setGoogleStatus(statusRes.data);
      setTodaySteps(todayRes.data);
      setWeeklyData(weeklyRes.data.weekly_data || []);
    } catch (error) {
      console.error('Failed to fetch step data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async () => {
    if (!googleStatus.connected) {
      setNotification({ type: 'error', message: 'Please connect Google Fit first' });
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    // Shared helper — same call the LogActivityPage StepsSyncPanel uses,
    // so both entry points can't diverge. Do not re-inline this logic.
    const result = await syncGoogleFitSteps();
    if (result.ok) {
      setLastSyncTime(new Date());
      setSyncResult({ success: true, stepsFound: result.steps });
      await fetchData();
      setNotification({ type: 'success', message: 'Sync completed' });
    } else if (result.needsConnect) {
      setSyncResult({ success: false, stepsFound: 0 });
      setNotification({ type: 'error', message: 'Please connect Google Fit first.' });
    } else {
      console.error('Sync failed:', result.error);
      setSyncResult({ success: false, stepsFound: 0 });
      setNotification({ type: 'error', message: result.error || 'Sync failed. Please try again.' });
    }
    setSyncing(false);
  };

  const formatLastSyncTime = () => {
    if (!lastSyncTime) return null;
    const now = new Date();
    const diff = Math.floor((now - lastSyncTime) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    return lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleConnectGoogle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/fitness/google/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      setNotification({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to initiate connection. Please check OAuth configuration.' 
      });
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!(await confirmDialog({ title: 'Disconnect Google Fit?', message: 'You will stop syncing step data until you reconnect.', confirmText: 'Disconnect', danger: true }))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/fitness/google/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGoogleStatus({ ...googleStatus, connected: false });
      setNotification({ type: 'success', message: 'Google Fit disconnected' });
      fetchData();
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const handleManualSubmit = async () => {
    const steps = parseInt(manualSteps);
    if (!steps || steps <= 0) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/fitness/steps/manual?steps=${steps}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotification({ type: 'success', message: 'Steps logged successfully!' });
      setShowManualInput(false);
      setManualSteps('');
      fetchData();
    } catch (error) {
      console.error('Failed to log steps:', error);
      setNotification({ type: 'error', message: 'Failed to log steps' });
    }
  };

  // Calculate max steps for chart scaling
  const maxSteps = Math.max(...weeklyData.map(d => d.steps), 10000);

  if (loading) {
    return (
      <div className="page-container">
        <TopHeader title="Step Tracking" showBack={true} />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div style={{ textAlign: 'center', color: COLORS.textSecondary }}>Loading...</div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-container">
      <TopHeader title="Step Tracking" showBack={true} />
      
      <div className="content-wrapper">
        <div className="content-section" style={{ padding: '0 16px 100px' }}>
          
          {/* Notification */}
          {notification && (
            <div style={{
              padding: '12px 16px',
              borderRadius: 12,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: notification.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${notification.type === 'success' ? COLORS.success : COLORS.error}`
            }}>
              {notification.type === 'success' ? 
                <Check size={18} color={COLORS.success} /> : 
                <AlertCircle size={18} color={COLORS.error} />
              }
              <span style={{ fontSize: 13, color: notification.type === 'success' ? COLORS.success : COLORS.error }}>
                {notification.message}
              </span>
              <button 
                onClick={() => setNotification(null)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                ×
              </button>
            </div>
          )}

          {/* Today's Steps Card */}
          <div style={{
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
            borderRadius: 20,
            padding: '24px 20px',
            marginBottom: 20,
            color: 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Footprints size={20} />
              <span style={{ fontSize: 14, fontWeight: 500, opacity: 0.9 }}>Today's Steps</span>
            </div>
            
            <div style={{ fontSize: 48, fontWeight: 700, marginBottom: 8 }}>
              {todaySteps.steps?.toLocaleString() || 0}
            </div>
            
            <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>
              Goal: {(todaySteps.goal || 10000).toLocaleString()} steps
            </div>
            
            {/* Progress Bar */}
            <div style={{
              height: 8,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 4,
              overflow: 'hidden',
              marginBottom: 8
            }}>
              <div style={{
                height: '100%',
                width: `${todaySteps.progress_percentage || 0}%`,
                background: 'white',
                borderRadius: 4,
                transition: 'width 0.5s ease'
              }} />
            </div>
            
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              {todaySteps.progress_percentage || 0}% of daily goal
            </div>
          </div>

          {/* Connection Status Cards */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>
              Connected Devices
            </h3>
            
            {/* Android - Google Fit */}
            <div style={{
              background: COLORS.white,
              borderRadius: 16,
              border: `1px solid ${COLORS.border}`,
              padding: '16px',
              marginBottom: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: googleStatus.connected ? COLORS.primaryLight : COLORS.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Activity size={22} color={googleStatus.connected ? COLORS.primary : COLORS.textMuted} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                      Google Fit
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                      Android
                    </div>
                  </div>
                </div>
                
                {googleStatus.connected ? (
                  <button
                    onClick={handleDisconnectGoogle}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 14px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: COLORS.error,
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    <Link2Off size={16} />
                    Disconnect
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      data-testid="google-fit-setup-toggle"
                      onClick={() => setShowGoogleInstructions((v) => !v)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px',
                        background: COLORS.purpleLight, color: COLORS.purple,
                        border: 'none', borderRadius: 8,
                        fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      }}
                    >
                      <Info size={16} />
                      Setup
                    </button>
                    <button
                      onClick={handleConnectGoogle}
                      disabled={!googleStatus.configured}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        background: googleStatus.configured ? COLORS.primary : COLORS.border,
                        color: googleStatus.configured ? COLORS.white : COLORS.textMuted,
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: googleStatus.configured ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <Link2 size={16} />
                      Connect
                    </button>
                  </div>
                )}
              </div>

              {/* Google Fit Setup Instructions */}
              {!googleStatus.connected && showGoogleInstructions && (
                <div
                  data-testid="google-fit-instructions"
                  style={{
                    marginTop: 16,
                    padding: '16px',
                    background: COLORS.background,
                    borderRadius: 12,
                  }}
                >
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>
                    Google Fit Setup Instructions
                  </h4>
                  <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.7 }}>
                    <li style={{ marginBottom: 8 }}>
                      Download Google Fit —{' '}
                      <a
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.fitness"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: COLORS.primary, textDecoration: 'underline' }}
                      >
                        Android
                      </a>{' '}
                      |{' '}
                      <a
                        href="https://apps.apple.com/us/app/google-fit-activity-tracker/id1433864494"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: COLORS.primary, textDecoration: 'underline' }}
                      >
                        iOS
                      </a>
                    </li>
                    <li style={{ marginBottom: 8 }}>Open Google Fit and sign in with your preferred email</li>
                    <li style={{ marginBottom: 8 }}>
                      Come back to socialstar, go to <strong>More → Step Tracking</strong>
                    </li>
                    <li style={{ marginBottom: 8 }}>
                      Tap <strong>Setup</strong> and choose the same account
                    </li>
                    <li style={{ marginBottom: 8 }}>
                      A screen will appear asking if you trust this app — tap <strong>Continue</strong> to proceed
                    </li>
                    <li>Allow all permissions when prompted</li>
                  </ol>
                </div>
              )}
              
              {googleStatus.connected && (
                <div style={{ marginTop: 12 }}>
                  {/* Sync Button and Status */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <button
                      onClick={handleSyncNow}
                      disabled={syncing}
                      data-testid="sync-now-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '10px 16px',
                        background: syncing ? COLORS.border : COLORS.primary,
                        color: syncing ? COLORS.textMuted : COLORS.white,
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: syncing ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <RefreshCw 
                        size={16} 
                        style={{ 
                          animation: syncing ? 'spin 1s linear infinite' : 'none' 
                        }} 
                      />
                      {syncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                    
                    {/* Last Sync Time */}
                    {lastSyncTime && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 4,
                        fontSize: 11,
                        color: COLORS.textSecondary
                      }}>
                        <Clock size={12} />
                        <span>Last sync: {formatLastSyncTime()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {!googleStatus.configured && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: COLORS.orangeLight,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}>
                  <Info size={14} color={COLORS.orange} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: '#92400E', lineHeight: 1.4 }}>
                    Google OAuth not configured. Admin needs to set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.
                  </span>
                </div>
              )}
            </div>

            {/* iOS - Apple Health */}
            <div style={{
              background: COLORS.white,
              borderRadius: 16,
              border: `1px solid ${COLORS.border}`,
              padding: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: COLORS.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Apple size={22} color={COLORS.textMuted} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>
                      Apple Health
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                      iOS
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowIOSInstructions(!showIOSInstructions)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    background: COLORS.purpleLight,
                    color: COLORS.purple,
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  <Info size={16} />
                  Setup
                </button>
              </div>
              
              {/* iOS Instructions */}
              {showIOSInstructions && (
                <div style={{
                  marginTop: 16,
                  padding: '16px',
                  background: COLORS.background,
                  borderRadius: 12
                }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>
                    iOS Setup Instructions
                  </h4>
                  <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.8 }}>
                    <li>Coming Soon</li>
                 </ol>
             
                </div>
              )}
            </div>
          </div>

          {/* Manual Entry */}
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => setShowManualInput(!showManualInput)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                background: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Plus size={18} color={COLORS.primary} />
                <span style={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>
                  Log Steps Manually
                </span>
              </div>
              <ChevronRight 
                size={18} 
                color={COLORS.textMuted}
                style={{ transform: showManualInput ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              />
            </button>
            
            {showManualInput && (
              <div style={{
                marginTop: -1,
                padding: '16px',
                background: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderTop: 'none',
                borderRadius: '0 0 12px 12px'
              }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="number"
                    value={manualSteps}
                    onChange={(e) => setManualSteps(e.target.value)}
                    placeholder="Enter steps"
                    style={{
                      flex: 1,
                      padding: '12px 14px',
                      fontSize: 15,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 8,
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleManualSubmit}
                    disabled={!manualSteps}
                    style={{
                      padding: '12px 20px',
                      background: manualSteps ? COLORS.primary : COLORS.border,
                      color: manualSteps ? COLORS.white : COLORS.textMuted,
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: manualSteps ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Weekly Overview */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, margin: 0 }}>
                This Week
              </h3>
              <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                Avg: {weeklyData.length > 0 ? Math.round(weeklyData.reduce((a, b) => a + b.steps, 0) / 7).toLocaleString() : 0} steps
              </div>
            </div>
            
            <div style={{
              background: COLORS.white,
              borderRadius: 16,
              border: `1px solid ${COLORS.border}`,
              padding: '20px 16px'
            }}>
              {/* Chart */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-end', 
                justifyContent: 'space-between',
                height: 120,
                marginBottom: 12
              }}>
                {weeklyData.map((day, index) => {
                  const height = maxSteps > 0 ? Math.max(8, (day.steps / maxSteps) * 100) : 8;
                  const isGoalMet = day.steps >= 10000;
                  
                  return (
                    <div 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        flex: 1
                      }}
                    >
                      <div style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: day.steps > 0 ? COLORS.text : COLORS.textMuted,
                        marginBottom: 6
                      }}>
                        {day.steps > 0 ? (day.steps / 1000).toFixed(1) + 'k' : '-'}
                      </div>
                      <div style={{
                        width: 28,
                        height: `${height}%`,
                        minHeight: 8,
                        background: day.is_today 
                          ? `linear-gradient(to top, ${COLORS.primary}, ${COLORS.primaryDark})`
                          : isGoalMet 
                            ? COLORS.success
                            : day.steps > 0 
                              ? COLORS.primaryLight
                              : COLORS.background,
                        borderRadius: 6,
                        border: day.is_today ? 'none' : `1px solid ${day.steps > 0 ? COLORS.primary : COLORS.border}`,
                        transition: 'height 0.3s ease'
                      }} />
                    </div>
                  );
                })}
              </div>
              
              {/* Day Labels */}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {weeklyData.map((day, index) => (
                  <div 
                    key={index}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      fontSize: 11,
                      fontWeight: day.is_today ? 600 : 400,
                      color: day.is_today ? COLORS.primary : COLORS.textMuted
                    }}
                  >
                    {day.day}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Goal Indicator */}
          <div style={{
            background: COLORS.orangeLight,
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <Target size={20} color={COLORS.orange} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>
                Daily Goal: 10,000 steps
              </div>
              <div style={{ fontSize: 11, color: '#B45309' }}>
                Walk more to earn bonus points on your actions!
              </div>
            </div>
          </div>

        </div>
      </div>
      
      <BottomNav />
    </div>
  );
};

export default StepTrackingPage;
